import type { PropsWithChildren } from 'react';
import { cache } from 'react';
import { dehydrate, QueryClient } from 'react-query';
import { redirect } from 'next/navigation';

import { reactQueryDefaultOptions } from '~/libs/reactQuery';
import { ENV } from '~/utils/env';
import { isServer } from '~/utils/ssr';

import { HydrateClient } from './HydrateClient';

function getAuthCookieName(): string {
  switch (ENV) {
    case 'prod':
    case 'stg':
      return 'snowdon_production_jwt';
    case 'dev':
    default:
      return 'snowdon_dev_jwt';
  }
}

function hasServerAuthCookie(): boolean {
  if (!isServer()) return true;

  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require, @typescript-eslint/no-require-imports
    const nextHeaders = require('next/headers');
    const cookieName = getAuthCookieName();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const authCookie = nextHeaders.cookies().get(cookieName);
    return !!authCookie;
  } catch {
    return false;
  }
}

type Query = {
  queryKey: readonly unknown[];
  queryFn: () => Promise<unknown>;
  /** true일 경우 prefetchInfiniteQuery 사용 */
  infinite?: boolean;
};

const getQueryClient = cache(
  () =>
    new QueryClient({
      defaultOptions: {
        queries: {
          ...reactQueryDefaultOptions,
          staleTime: 10 * 1000,
          cacheTime: Infinity,
        },
      },
    }),
);

type Props = {
  query?: Query | Array<Query | undefined>;
  skipAuthCheck?: boolean;
};

async function HydratePrefetchAsync({
  children,
  query,
  skipAuthCheck = false,
}: PropsWithChildren<Props>) {
  // 서버 사이드에서 인증 쿠키가 없으면 401 페이지로 리다이렉트
  if (!hasServerAuthCookie() && !skipAuthCheck) redirect('/401');

  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (!query) return <>{children}</>;

  const queryClient = getQueryClient();

  const prefetch = (item: Query) =>
    item.infinite
      ? queryClient.prefetchInfiniteQuery(item.queryKey, item.queryFn)
      : queryClient.prefetchQuery(item.queryKey, item.queryFn);

  if (Array.isArray(query)) {
    await Promise.all(
      query
        .filter((item): item is Query => typeof item !== 'undefined')
        .map(prefetch),
    );
  } else {
    await prefetch(query);
  }

  return (
    <HydrateClient state={dehydrate(queryClient)}>{children}</HydrateClient>
  );
}

// Async Server Component wrapper for TypeScript compatibility
export const HydratePrefetch = HydratePrefetchAsync as unknown as (
  props: PropsWithChildren<Props>,
) => JSX.Element;
